import "./files.styl";
import { sidebarCollapseController } from "../_resources/ui-library.js";
import { holdButton } from "../_resources/ui-library.js";
import { backfillSubtab, setSubtab } from "../_resources/url-state.js";
import { supabaseClient as supabase } from "../auth/supabase.service";

const STORAGE_BUCKET = "public-files";

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
}

async function getCurrentUserId(){
  try {
    const { data } = await supabase.auth.getUser();
    return data?.user?.id;
  } catch(e){}
  return null;
}

const FileModel = {
  async fetch() {
    const { data, error } = await supabase
      .from('files')
      .select('id, name, path, url, size, content_type, user_id, public, created_at');
    return { data, error };
  },

  async upload(file) {
    if (!file) return { error: "No file provided" };
    const userId = await getCurrentUserId() || "anon";
    const date = (new Date(Date.now())).toISOString()
      .replaceAll("-", "")
      .replace("T", "")
      .replaceAll(":", "")
      .replace(/.\d\d\dZ/, "");
    const path = `${userId}/${date}_${file.name}`;

    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from(STORAGE_BUCKET)
      .upload(path, file, { upsert: false });

    if (uploadError) return { error: uploadError };

    let publicUrl;
    try {
      const res = await supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      publicUrl = res?.publicURL || res?.data?.publicUrl || null;
    } catch (_) {
      publicUrl = null;
    }

    const { data, error } = await supabase
      .from('files')
      .insert([{
        name: file.name,
        path,
        url: publicUrl,
        size: file.size,
        content_type: file.type,
        user_id: userId,
        public: true
      }])
      .single();

    return { data, error };
  },

  async remove(id, path) {
    const errors = [];
    if (path) {
      const { error: rmErr } = await supabase.storage.from(STORAGE_BUCKET).remove([path]);
      if (rmErr) errors.push(rmErr);
    }
    const { error } = await supabase.from('files').delete().eq('id', id);
    if (error) errors.push(error);
    return { error: errors.length ? errors : null };
  },

};

function ensureFontFace(id, url, formatHint) {
  if (!url) return null;
  const styleId = `filefont-${id}`;
  if (document.getElementById(styleId)) return styleId;
  const fmt = formatHint ? ` format("${formatHint}")` : '';
  const css = `
    @font-face {
      font-family: "${styleId}";
      src: url("${url}")${fmt};
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;
  const s = document.createElement('style');
  s.id = styleId;
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);
  return styleId;
}

function guessFormatFromName(name) {
  if (!name) return null;
  const ext = name.split('.').pop().toLowerCase();
  switch(ext) {
    case 'woff2': return 'woff2';
    case 'woff': return 'woff';
    case 'ttf': return 'truetype';
    case 'otf': return 'opentype';
    default: return null;
  }
}

export default function FilesView({ dom, message, getToken }){
  const { bad, info, progress, good } = message;
  const panel = d3.select(dom + " .contentSection");
  sidebarCollapseController(dom);

  const Controller = {
    async load() {
      const { data, error } = await FileModel.fetch();
      if (error) {
        panel.html('');
        return bad('Error loading files: ' + JSON.stringify(error));
      }
      renderTable(data || []);
    },

    async uploadFromInput(inputEl) {
      const file = inputEl.node().files && inputEl.node().files[0];
      if (!file) return bad("Select a file first");
      progress("Uploading...");
      const { data, error } = await FileModel.upload(file);
      if (error) return bad("Upload failed: " + JSON.stringify(error));
      good("File uploaded", 3000);
      inputEl.node().value = "";
      this.load();
    },

    async deleteFile(file) {
      const { error } = await FileModel.remove(file.id, file.path);
      if (error) return bad("Error deleting file: " + JSON.stringify(error));
      good("File deleted", 3000);
      this.load();
    },

  };

  function render() {
    panel.html('');

    const controls = panel.append('div').attr('class', 'files-controls');
    controls.append('label').text('Upload file: ');
    const fileInput = controls.append('input').attr('type', 'file').attr('class', 'file-input');
    const uploadBtn = controls.append('span').attr('class', 'button').text('Upload');
    uploadBtn.on('click', async () => await Controller.uploadFromInput(fileInput));

    backfillSubtab("files");
    setSubtab("files");
    Controller.load();
  }

  function renderTable(files) {
    panel.selectAll('.files-table').remove();

    const table = panel.append('table').attr('class', 'admin-table files-table');
    if (files.length > 0) {
      table.append('tr').call(rowEl => {
        rowEl.append('th').text('preview');
        rowEl.append('th').text('name');
        rowEl.append('th').text('size');
        rowEl.append('th').text('type');
        rowEl.append('th').text('created');
        rowEl.append('th').text('');
      });
    }

    table.selectAll('tr.file-row')
      .data((files || []).slice().sort((a,b) => new Date(b.created_at) - new Date(a.created_at)))
      .enter()
      .append('tr')
      .attr('class', 'data-row file-row')
      .each(function(d) {
        const row = d3.select(this);

        const previewCell = row.append('td').attr('class', 'preview-cell');
        if (d.content_type && d.content_type.startsWith('image/') && d.url) {
          previewCell.append('img')
            .attr('src', d.url)
            .style('max-width', '120px')
            .style('max-height', '60px')
            .style('object-fit', 'contain');
        } else if ((d.content_type && d.content_type.includes('font')) || /\.(ttf|otf|woff2?|eot)$/i.test(d.name)) {
          if (d.url) {
            const format = guessFormatFromName(d.name);
            const fontName = ensureFontFace(d.id, d.url, format);
            previewCell.append('span')
              .text('Aa')
              .style('font-family', fontName || 'inherit')
              .style('font-size', '22px')
              .style('display', 'inline-block')
              .style('padding', '6px 8px');
          } else {
            previewCell.text('font');
          }
        } else if (d.content_type === 'application/pdf' && d.url) {
          previewCell.append('a').attr('href', d.url).attr('target', '_blank').text('View PDF');
        } else {
          const ext = (d.name || '').split('.').pop();
          previewCell.append('div').attr('class', 'file-icon').text(ext || 'file');
        }

        const linkCell = row.append('td');
        if (d.url) {
          linkCell.append('a')
            .attr('href', d.url)
            .attr('target', '_blank')
            .text(d.name);
        } else {
          linkCell.text(d.name + " (no public url)");
        }

        row.append('td').text(fmtSize(d.size));
        row.append('td').text(d.content_type || "");
        row.append('td').text(d.created_at ? new Date(d.created_at).toLocaleString() : "");

        const actionsCell = row.append('td').attr('class', 'actions-cell').append('div').classed('actionsbox micro', true);

        const delBtn = actionsCell.append('span')
          .attr('class', 'button delete hold-btn')
          .text('✘');
        holdButton(delBtn, async () => await Controller.deleteFile(d));
      });
  }

  return { render };
}